import { create } from 'zustand'
import { apiClient, setAccessToken } from '@/lib/api'
import { queryClient } from '@/lib/queryClient'

export interface PlatformSession {
  accessToken: string
  expiresAtUtc: string
  displayName: string
  email: string
  /** The password in force was issued rather than chosen. The panel holds until it is replaced. */
  mustChangePassword: boolean
  /** Null means it has never been changed from the one the first startup generated. */
  passwordChangedAtUtc: string | null
}

interface PlatformSessionState {
  session: PlatformSession | null
  /** True until the initial silent refresh has settled, so the panel can hold the route. */
  isRestoring: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  restore: () => Promise<void>
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>
}

/**
 * Session for the platform operator's own panel — the people who create tenants, not the
 * people who run a till.
 */
/**
 * Backed by its own httpOnly refresh cookie, scoped to `/api/platform` so it never rides along
 * on an ordinary tenant request — see `PlatformAuthenticationService.RefreshAsync`. Refreshing
 * the page used to sign the operator straight out, because there was nothing durable to
 * exchange; `restore()` is what a reload now calls to pick the session back up, the same way a
 * tenant session does.
 *
 * The access token itself still goes through the same in-memory holder in `lib/api.ts` that a
 * tenant session uses, since only one of the two is ever the active context in a given tab. A
 * request made from the wrong context is refused by the server (a platform token carries no
 * tenant claim and vice versa) and surfaces as an ordinary `ApiError`, so nothing here needs to
 * defend against it beyond bouncing back to this login page when that happens.
 */
export const usePlatformSession = create<PlatformSessionState>((set) => ({
  session: null,
  isRestoring: true,

  login: async (email, password) => {
    const session = await apiClient.post<PlatformSession>('/api/platform/login', {
      email: email.trim(),
      password,
    })

    setAccessToken(session.accessToken)
    set({ session, isRestoring: false })
  },

  logout: async () => {
    try {
      await apiClient.post('/api/platform/logout')
    } finally {
      setAccessToken(null)
      set({ session: null })
      // Same reasoning as the tenant session's logout — the next operator to sign in on this
      // device must not see this one's cached tenant list.
      queryClient.clear()
    }
  },

  /**
   * Changes the operator's own password.
   *
   * The server ends every session the old password opened, this one included, and hands back a
   * replacement — so the answer is stored the same way a login's is. Without that the operator
   * would be signed out by the act of securing the account, which is a good way to teach somebody
   * not to bother.
   */
  changePassword: async (currentPassword, newPassword) => {
    const session = await apiClient.post<PlatformSession>('/api/platform/change-password', {
      currentPassword,
      newPassword,
    })

    setAccessToken(session.accessToken)
    set({ session })
  },

  restore: async () => {
    try {
      // Succeeds only if the httpOnly refresh cookie is still valid, which is what makes a
      // page reload keep the operator signed in without any token in localStorage.
      const session = await apiClient.post<PlatformSession>('/api/platform/refresh')
      setAccessToken(session.accessToken)
      set({ session })
    } catch {
      set({ session: null })
    } finally {
      set({ isRestoring: false })
    }
  },
}))
